import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {Annex} from '../../models/api/annex.model';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {AnnexActions} from './annex.actions';
import {AnnexService} from '../../services/annex.service';
import {catchError, map, tap} from 'rxjs/operators';
import {Injectable, NgZone} from '@angular/core';
import {FileService} from '../../services/file.service';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '../../services/notification.service';
import {SearchListActions} from '../search-list/search-list.actions';
import {SearchListEnum} from '../../enums/search-list.enum';
import {AnnexRejectComponent} from '../../modules/annex/components/annex-reject';
import {ValidationActions} from '../../modules/validation/state/validation.actions';
import {BulkConfirmModalComponent} from '../../modules/shared/components/bulk-confirm-modal';
import {BulkSummaryModalComponent} from '../../modules/shared/components/bulk-summary-modal';
import {handleBulkError, handleBulkSuccess} from '../../helpers/bulk-operation.helper';
import {ProtectionPeriodCorrectionModalComponent} from '../../modules/annex/components/protection-period-correction/protection-period-correction-modal.component';
import {ProtectionPeriodCorrectionDto} from '../../models/api/protection-period-correction-dto.model';
import {PolicyService} from '../../services/policy.service';
import {DescriptiveAnnexUpdateDto} from '../../models/api/annex/descriptive-annex-update-dto.model';
import {HttpErrorResponse} from '@angular/common/http';
import {of, throwError} from 'rxjs';
import {skipInterceptors} from '../../helpers/request.helper';
import {RiskDefinition} from '../../models/api/riskDefinition.model';

export interface AnnexStateModel extends ValidationStateModel {
    activeAnnex: Annex;
    protectionPeriodCorrectionResult: ProtectionPeriodCorrectionDto[] | null;
    protectionPeriodCorrectionError: string | null;
    risksDefinition?: RiskDefinition;
}

@State<AnnexStateModel>({
    name: AnnexState.STATE_NAME,
    defaults: {
        activeAnnex: null,
        validationErrors: null,
        protectionPeriodCorrectionResult: null,
        protectionPeriodCorrectionError: null,
        risksDefinition: null,
    },
})
@Injectable()
export class AnnexState {
    static readonly STATE_NAME = 'annex';

    constructor(
        private annexService: AnnexService,
        private fileService: FileService,
        private modalService: ModalService,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private store: Store,
        protected zone: NgZone,
        protected policyService: PolicyService
    ) {}

    @Selector()
    static activeAnnex(state: AnnexStateModel) {
        return state.activeAnnex;
    }

    @Selector()
    static validationErrors(state: AnnexStateModel) {
        return state.validationErrors;
    }

    @Selector()
    static protectionPeriodCorrectionResult(state: AnnexStateModel) {
        return state.protectionPeriodCorrectionResult;
    }

    @Selector()
    static protectionPeriodCorrectionError(state: AnnexStateModel) {
        return state.protectionPeriodCorrectionError;
    }

    @Selector()
    static risksDefinition(state: AnnexStateModel) {
        return state.risksDefinition;
    }

    @Action(AnnexActions.SetRisksDefinition)
    setRisksDefinition(ctx: StateContext<AnnexStateModel>, {risksDefinition}: AnnexActions.SetRisksDefinition) {
        ctx.patchState({
            risksDefinition,
        });
    }

    @Action(AnnexActions.GetById)
    getAnnex(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.GetById) {
        return this.annexService.getById(annexId).pipe(
            tap((activeAnnex) => {
                ctx.patchState({activeAnnex});
            })
        );
    }

    @Action(AnnexActions.DownloadAnnexList)
    downloadAnnexList(ctx: StateContext<AnnexStateModel>, {params}: AnnexActions.DownloadAnnexList) {
        return this.annexService.downloadAnnexList(params).pipe(
            map((file) => {
                return this.fileService.getFileByHash(file.hash);
            })
        );
    }

    @Action(AnnexActions.Print)
    printPolicy(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.Print) {
        return this.annexService.print(annexId).pipe(
            tap((file) => {
                this.fileService.getFileByHash(file.hash);
            })
        );
    }

    @Action(AnnexActions.ShowAcceptModal)
    showAcceptModal(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.ShowAcceptModal) {
        this.modalService.confirm({
            nzContent: this.translate.instant(`ANNEX.ACCEPT_TEXT`),
            nzOkText: this.translate.instant('LABELS.CONFIRM'),
            nzCancelText: this.translate.instant('LABELS.CANCEL'),
            nzTitle: this.translate.instant('LABELS.CONFIRMATION'),
            nzOnOk: () => {
                return ctx.dispatch(new AnnexActions.Accept(annexId));
            },
        });
    }

    @Action(AnnexActions.Accept)
    accept(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.Accept) {
        return this.annexService.approveAnnex(annexId, skipInterceptors([409])).pipe(
            tap(() => {
                this.notificationService.success('ANNEX.ANNEX_APPROVED');
                ctx.dispatch(new AnnexActions.GetById(annexId));
                ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.ANNEX));
            }),
            catchError((reject: HttpErrorResponse) => {
                if (typeof reject?.error?.message === 'string') {
                    this.notificationService.error(reject.error.message);
                }
                return of(null);
            })
        );
    }

    @Action(AnnexActions.ShowVerifyModal)
    showVerifyModal(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.Verify) {
        this.modalService.confirm({
            nzContent: this.translate.instant(`ANNEX.VERIFY_TEXT`),
            nzOkText: this.translate.instant('LABELS.CONFIRM'),
            nzCancelText: this.translate.instant('LABELS.CANCEL'),
            nzTitle: this.translate.instant('LABELS.CONFIRMATION'),
            nzOnOk: () => {
                return ctx.dispatch(new AnnexActions.Verify(annexId));
            },
        });
    }

    @Action(AnnexActions.Verify)
    verify(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.Verify) {
        return this.annexService.verifyAnnex(annexId).pipe(
            tap(() => {
                this.notificationService.success('ANNEX.ANNEX_VERIFIED');
                ctx.dispatch(new AnnexActions.GetById(annexId));
                ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.ANNEX));
            })
        );
    }

    @Action(AnnexActions.ShowRejectModal)
    showRejectModal(ctx: StateContext<AnnexStateModel>, {annexId}: AnnexActions.ShowRejectModal) {
        this.modalService.generic({
            nzContent: AnnexRejectComponent,
            nzOkText: this.translate.instant('LABELS.CONFIRM'),
            nzCancelText: this.translate.instant('LABELS.CANCEL'),
            nzTitle: this.translate.instant('ANNEX.REJECT_TITLE'),
            nzWidth: '50%',
            nzOnOk: (component: AnnexRejectComponent) => {
                return ctx.dispatch(new AnnexActions.Reject(annexId, component.annexRejectModel));
            },
        });
    }

    @Action(AnnexActions.Reject)
    reject(ctx: StateContext<AnnexStateModel>, {annexId, annexReject}: AnnexActions.Reject) {
        return this.annexService.rejectAnnex(annexId, annexReject).pipe(
            tap(() => {
                this.notificationService.success('ANNEX.ANNEX_REJECTED');
                ctx.dispatch(new AnnexActions.GetById(annexId));
                ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.ANNEX));
            }),
            catchError(({error}) => {
                return ctx.dispatch(new ValidationActions.Set(AnnexState.STATE_NAME, error));
            })
        );
    }

    // BULK OPERATIONS

    @Action(AnnexActions.ConfirmBulkOperation)
    openBulkConfirmModal(
        ctx: StateContext<AnnexStateModel>,
        {modalOptions, payload, action}: AnnexActions.ConfirmBulkOperation
    ) {
        this.modalService.generic({
            nzContent: BulkConfirmModalComponent,
            nzTitle: this.translate.instant(modalOptions.title),
            nzWidth: '50%',
            nzCancelText: this.translate.instant('LABELS.NO'),
            nzOkText: this.translate.instant('LABELS.YES'),
            nzData: {
                payload,
                message: modalOptions.message,
            },
            nzOnOk: () => {
                ctx.dispatch(action);
            },
        });
    }

    @Action(AnnexActions.SummarizeBulkOperation)
    openBulkSummaryModal(
        ctx: StateContext<AnnexStateModel>,
        {modalOptions, bulkOperation, isPrinting}: AnnexActions.SummarizeBulkOperation
    ) {
        this.modalService.generic({
            nzContent: BulkSummaryModalComponent,
            nzTitle: this.translate.instant(modalOptions.title),
            nzWidth: '50%',
            nzData: {
                bulkOperation,
                type: modalOptions.type,
            },
            nzOnOk: () => {
                if (bulkOperation?.doable?.length) {
                    if (isPrinting) {
                        const payload = {
                            allSelected: false,
                            ids: [],
                            numbers: [],
                        };
                        bulkOperation?.doable?.forEach((item) => {
                            payload.ids.push(item?.id);
                            payload.numbers.push(item?.number);
                        });
                        ctx.dispatch(new AnnexActions.BulkPrint(payload));
                    }
                    ctx.dispatch(new SearchListActions.GetListPage(SearchListEnum.ANNEX));
                }
            },
        });
    }

    @Action(AnnexActions.BulkPrint)
    printBulk(ctx: StateContext<AnnexStateModel>, {payload}: AnnexActions.BulkPrint) {
        return this.annexService.bulkPrint(payload).pipe(
            tap((file) => {
                // @ts-ignore
                this.fileService.getFileByHash(file.hash);
            }),
            handleBulkError(
                this.store,
                (error) =>
                    new AnnexActions.SummarizeBulkOperation(
                        {title: 'ANNEX.BULK_PRINT_TITLE', type: 'ANNEX.ANNEXES'},
                        error,
                        true
                    )
            )
        );
    }

    @Action(AnnexActions.BulkAccept)
    acceptBulk(ctx: StateContext<AnnexStateModel>, {payload}: AnnexActions.BulkAccept) {
        return this.annexService.bulkApprove(payload).pipe(
            handleBulkSuccess(SearchListEnum.ANNEX, this.notificationService, this.store),
            handleBulkError(
                this.store,
                (error) =>
                    new AnnexActions.SummarizeBulkOperation(
                        {title: 'ANNEX.BULK_ACCEPT_TITLE', type: 'ANNEX.ANNEXES'},
                        error
                    )
            )
        );
    }

    @Action(AnnexActions.BulkReject)
    rejectBulk(ctx: StateContext<AnnexStateModel>, {payload}: AnnexActions.BulkReject) {
        return this.annexService.bulkReject(payload).pipe(
            handleBulkSuccess(SearchListEnum.ANNEX, this.notificationService, this.store),
            handleBulkError(
                this.store,
                (error) =>
                    new AnnexActions.SummarizeBulkOperation(
                        {title: 'ANNEX.BULK_REJECT_TITLE', type: 'ANNEX.ANNEXES'},
                        error
                    )
            )
        );
    }

    @Action(AnnexActions.Clear)
    clear(ctx: StateContext<AnnexStateModel>) {
        ctx.patchState({
            activeAnnex: null,
            validationErrors: null,
            protectionPeriodCorrectionResult: null,
        });
    }

    @Action(AnnexActions.ProtectionPeriodCorrectionModal)
    protectionPeriodCorrection(ctx: StateContext<AnnexStateModel>) {
        return this.zone.run(
            () =>
                this.modalService.generic({
                    nzContent: ProtectionPeriodCorrectionModalComponent,
                    nzTitle: this.translate.instant('ANNEX.PROTECTION_PERIOD_CORRECTION_TITLE'),
                    nzOkText: null,
                    nzCancelText: this.translate.instant('LABELS.CLOSE'),
                    nzWidth: '70%',
                }).afterClose
        );
    }

    @Action(AnnexActions.CorrectProtectionPeriod)
    correctProtectionPeriod(ctx: StateContext<AnnexStateModel>, {file}: AnnexActions.CorrectProtectionPeriod) {
        return this.policyService.protectionPeriodCorrection(file).pipe(
            tap((response) => {
                ctx.patchState({
                    protectionPeriodCorrectionResult: response,
                });
            }),
            catchError((reject: HttpErrorResponse) => {
                if (reject.status === 409 && typeof reject.error?.message === 'string') {
                    ctx.patchState({
                        protectionPeriodCorrectionError: reject.error?.message,
                    });
                }
                return throwError(() => reject);
            })
        );
    }

    @Action(AnnexActions.ClearProtectionPeriodCorrectionResult)
    clearProtectionPeriodCorrectionResult(ctx: StateContext<AnnexStateModel>) {
        ctx.patchState({
            protectionPeriodCorrectionResult: null,
            protectionPeriodCorrectionError: null,
        });
    }

    @Action(AnnexActions.ChangeParagraphOne)
    changeParagraphOne(
        _: StateContext<AnnexStateModel>,
        {annexId, paragraphOneContent}: AnnexActions.ChangeParagraphOne
    ) {
        return this.annexService
            .updateParagraphOne(annexId, new DescriptiveAnnexUpdateDto({paragraphOneContent}), skipInterceptors([409]))
            .pipe(
                tap(() => {
                    this.notificationService.success(this.translate.instant('ANNEX.DESCRIPTIVE_CHANGE_UPDATE_SUCCESS'));
                }),
                catchError((reject: HttpErrorResponse) => {
                    if (typeof reject.error?.message === 'string') {
                        this.notificationService.error(reject.error?.message);
                    }
                    return of(null);
                })
            );
    }
}
